import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { appointmentAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useTranslation } from 'react-i18next';

const AppointmentsContext = createContext();

export const useAppointments = () => useContext(AppointmentsContext);

export const AppointmentsProvider = ({ children }) => {
    const { t } = useTranslation();
    const { user, isSuperAdmin, hasPrivilege } = useAuth();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        branchId: '',
        startDate: null,
        endDate: null,
        patientId: '',
        doctorId: '',
        representativeId: ''
    });
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0 });

    // Status color mapping only (no icons)
    const statusConfig = {
        colors: {
            scheduled: 'primary',
            in_progress: 'warning',
            completed: 'success',
            cancelled: 'error',
            no_show: 'default',
        }
    };

    // Calculate statistics
    const stats = useMemo(() => {
        const total = appointments.length;
        const completed = appointments.filter(a => a.status === 'completed').length;
        const pending = appointments.filter(a => a.status === 'scheduled').length;
        const cancelled = appointments.filter(a => a.status === 'cancelled').length;

        const dailyIncome = user?.userType !== 'radiologist'
            ? appointments.reduce((sum, apt) => {
                const price = apt.makeHugeSale
                    ? Number(apt.customPrice || 0)
                    : Number(apt.price || 0);
                return sum + price;
            }, 0)
            : 0;

        return { total, completed, pending, cancelled, dailyIncome };
    }, [appointments, user]);

    // Fetch appointments
    const fetchAppointments = useCallback(async (params = {}) => {
        if (!hasPrivilege('appointments', 'view')) {
            setError(t('appointments.noPrivilegeError'));
            return;
        }

        setLoading(true);
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const endOfToday = new Date(today);
            endOfToday.setHours(23, 59, 59, 999);

            const queryParams = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters,
                startDate: today.toISOString(),
                endDate: endOfToday.toISOString(),
                ...params
            };

            if (user?.userType === 'radiologist') {
                queryParams.radiologistId = user._id || user.id;
            }

            const res = await appointmentAPI.getAllAppointments(queryParams);
            const data = res.data.data?.appointments || res.data?.appointments || [];

            setAppointments(data);
            setPagination(prev => ({
                ...prev,
                total: res.data.data?.pagination?.total || res.data?.pagination?.total || 0
            }));
        } catch (err) {
            setError(err.response?.data?.message || t('appointments.fetchError'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, user, hasPrivilege, t]);

    // Create appointment
    const createAppointment = useCallback(async (formData) => {
        try {
            const newAppointment = await appointmentAPI.createAppointment(formData);
            setAppointments(prev => [newAppointment.data, ...prev]);
            return { success: true, data: newAppointment.data };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || t('appointments.createError')
            };
        }
    }, [t]);

    // Update appointment
    const updateAppointment = useCallback(async (id, formData) => {
        try {
            const updated = await appointmentAPI.updateAppointment(id, formData);
            setAppointments(prev =>
                prev.map(apt => apt._id === id ? updated.data : apt)
            );
            return { success: true, data: updated.data };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || t('appointments.updateError')
            };
        }
    }, [t]);

    // Update appointment status
    const updateAppointmentStatus = useCallback(async (id, statusData, isFormData = false) => {
        try {
            const updated = await appointmentAPI.updateAppointmentStatus(
                id,
                statusData,
                isFormData
            );
            setAppointments(prev =>
                prev.map(apt => apt._id === id ? updated.data : apt)
            );
            return { success: true, data: updated.data };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || t('appointments.statusUpdateError')
            };
        }
    }, [t]);

    // Delete appointment
    const deleteAppointment = useCallback(async (id) => {
        try {
            await appointmentAPI.deleteAppointment(id);
            setAppointments(prev => prev.filter(apt => apt._id !== id));
            return { success: true };
        } catch (err) {
            return {
                success: false,
                error: err.response?.data?.message || t('appointments.deleteError')
            };
        }
    }, [t]);

    // Update filters
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // Context value
    const value = {
        appointments,
        loading,
        error,
        filters,
        pagination,
        stats,
        statusConfig,
        setPagination,
        fetchAppointments,
        createAppointment,
        updateAppointment,
        updateAppointmentStatus,
        deleteAppointment,
        updateFilters
    };

    return (
        <AppointmentsContext.Provider value={value}>
            {children}
        </AppointmentsContext.Provider>
    );
};